/* ---------- AI provider settings ---------- */
/* Three tiers (blueprint02 §14B + DECISIONS 2026-06-02 flutter_gemma-first):
   local Gemma models are primary, cloud providers are fallback. */

export type ModelState = 'none' | 'downloading' | 'downloaded';
export type AiModel = {
  id: string;
  name: string;
  by: string;
  desc: string;
  size?: string; // present = downloadable on-device model
};
export type AiProviderId = 'local' | 'openrouter' | 'sota';
export type AiProvider = {
  id: AiProviderId;
  title: string;
  subtitle: string;
  badge?: string;
  tint: 'sage' | 'sky' | 'lilac';
  privacy: string;
  models: AiModel[];
};
export const AI_PROVIDERS: AiProvider[] = [{
  id: 'local',
  title: 'Local only',
  subtitle: 'Runs entirely on your phone',
  badge: 'Most private',
  tint: 'sage',
  privacy: 'Nothing leaves your phone, and it works offline. Models are downloaded once and stored on device.',
  models: [{
    id: 'gemma-3-1b',
    name: 'Gemma 3 1B',
    by: 'Google · flutter_gemma',
    desc: 'Fast and light — everyday navigation & search',
    size: '529 MB'
  }, {
    id: 'gemma-3n-e2b',
    name: 'Gemma 3n E2B',
    by: 'Google · flutter_gemma',
    desc: 'Better understanding, a little slower',
    size: '3.1 GB'
  }, {
    id: 'gemma-3n-e4b',
    name: 'Gemma 3n E4B',
    by: 'Google · flutter_gemma',
    desc: 'Best on-device quality',
    size: '4.4 GB'
  }]
}, {
  id: 'openrouter',
  title: 'OpenRouter',
  subtitle: 'Cloud — many models, one key',
  tint: 'sky',
  privacy: 'Your questions are sent to OpenRouter. Your health content, logs and care plan stay on your phone.',
  models: [{
    id: 'llama-3-3-70b',
    name: 'Llama 3.3 70B',
    by: 'Meta',
    desc: 'Open model, great value'
  }, {
    id: 'deepseek-v3',
    name: 'DeepSeek V3',
    by: 'DeepSeek',
    desc: 'Strong reasoning'
  }, {
    id: 'qwen-2-5-72b',
    name: 'Qwen 2.5 72B',
    by: 'Alibaba',
    desc: 'Fast and multilingual'
  }]
}, {
  id: 'sota',
  title: 'SoTA cloud',
  subtitle: 'The most capable models',
  tint: 'lilac',
  privacy: 'Your questions are sent to Anthropic or OpenAI. Your health content, logs and care plan stay on your phone.',
  models: [{
    id: 'claude',
    name: 'Claude',
    by: 'Anthropic',
    desc: 'Careful and warm with health language'
  }, {
    id: 'gpt',
    name: 'GPT',
    by: 'OpenAI',
    desc: 'Fast all-rounder'
  }]
}];

/* ---------- narration audio settings ---------- */
/* Tracks are produced in the admin web app (ElevenLabs) and published to
   cloud storage (Cloudflare R2). The app streams or downloads from there —
   either on demand (first play) or all at once from here.
   The Sleep & relaxation downloads are what the Relax & Sleep session
   picker's "On device" badges point at (card-flows §5). */

export const AUDIO_LIBRARY = {
  totalTracks: 38,
  totalSize: '412 MB',
  onDeviceTracks: 11,
  onDeviceSize: '104 MB',
  lastSync: 'Updated 2 hours ago',
  categories: [{
    id: 'asthma',
    name: 'Asthma essentials',
    tracks: 12,
    size: '128 MB',
    downloaded: 9
  }, {
    id: 'allergies',
    name: 'Allergies',
    tracks: 8,
    size: '92 MB',
    downloaded: 0
  }, {
    id: 'sleep',
    name: 'Sleep & relaxation',
    tracks: 11,
    size: '124 MB',
    downloaded: 2
  }, {
    id: 'wellbeing',
    name: 'Wellbeing',
    tracks: 7,
    size: '68 MB',
    downloaded: 0
  }]
};

/* ---------- other settings sections (blueprint02 §14 stubs) ---------- */

export const SETTINGS_STUBS = [{
  id: 'companion',
  title: 'Companion',
  value: 'Sunny'
}, {
  id: 'voice',
  title: 'Voice & narration style',
  value: 'Warm · 1.0×'
}, {
  id: 'accessibility',
  title: 'Accessibility',
  value: 'Subtitles on'
}];